import { lazy, Suspense, type ReactElement } from 'react';

import type { ContactsTierSelectProps } from '@ctct/payflow-ui';

import { Price, Heading, Button, Tag, TickIcon } from '../../../atoms';
import { PlanPriceBreakdown } from '../../PlanPriceBreakdown/PlanPriceBreakdown';
import type { MWebCardSelectProps } from '../types';

const IntegrationsBadge = lazy(() =>
  import('../../../atoms/Icon/IntegrationsBadge')
    .then((module) => ({ default: module.IntegrationsBadge }))
    .catch(() => ({ default: () => <span hidden /> }))
);

type Props = Pick<
  MWebCardSelectProps,
  | 'withCursorPointer'
  | 'optionCardSize'
  | 'value'
  | 'clickButtonToSelect'
  | 'onOptionSelect'
  | 'priceSize'
  | 'type'
  | 'showBulletTicks'
  | 'oldValuePlacement'
  | 'onRemoveSms'
> & {
  option: MWebCardSelectProps['options'][0];
  disabled?: boolean;
  contactsTierSelectWidget?: ReactElement<ContactsTierSelectProps>;
};

export const MWebCardSelectItem = ({
  clickButtonToSelect,
  optionCardSize,
  onOptionSelect,
  priceSize,
  option,
  disabled = false,
  contactsTierSelectWidget,
  showBulletTicks = false,
  oldValuePlacement,
  onRemoveSms,
}: Props) => {
  const showDescriptions = optionCardSize === 'large';
  const dataAttrs = option?.dataAttributes ?? {};

  const setupBulletDescription = (bullets: string[]) => {
    return (
      <ul className={`mweb-card-content__bullet-list ${showBulletTicks ? 'with-ticks' : ''}`}>
        {bullets.map((bullet, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={`${option.id}_bullet_${index}`} className="mweb-card-content__bullet-item">
            {showBulletTicks && <TickIcon className="mweb-card-content__bullet-tick" />}
            <span>{bullet}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="mweb-card-content">
      <div className="mweb-card-content__top">
        {option.value && (
          <div className="mweb-card-content__heading-row">
            <Heading level="h6" className="mweb-card-content__value">
              {option.value}
            </Heading>
            {option.highlightedText && <Tag variant="blue">{option.highlightedText}</Tag>}
            {option.secondaryTagText && <Tag variant="violet">{option.secondaryTagText}</Tag>}
          </div>
        )}
        {showDescriptions && (
          <div
            className={`mweb-card-content__description${
              option.mWebVariantContent?.startingAtLabel ? ' mweb-card-content__description--bold' : ''
            }`}
          >
            {option.mWebVariantContent?.heading}
          </div>
        )}
      </div>

      {contactsTierSelectWidget && <div className="mweb-card-content__widget">{contactsTierSelectWidget}</div>}

      {option.mWebVariantContent?.contactCapExceededMessage ? null : (
        <>
          {option.mWebVariantContent?.contactCapLabel && (
            <div className="mweb-card-content__contact-cap-label">{option.mWebVariantContent.contactCapLabel}</div>
          )}
          {option.mWebVariantContent?.startingAtLabel && (
            <div className="mweb-card-content__starting-at">{option.mWebVariantContent.startingAtLabel}</div>
          )}

          {option.price && (
            <Price
              {...option.price}
              oldValue={option.price?.oldValue}
              sizeLength={priceSize}
              className="with-mweb-variant"
              oldValuePlacement={oldValuePlacement}
            />
          )}

          {option.bottomPriceLabel ? (
            <div className="mweb-card-content__bottom-price-label">{option.bottomPriceLabel}</div>
          ) : null}

          {option.priceBreakdown && <PlanPriceBreakdown breakdown={option.priceBreakdown} onRemoveSms={onRemoveSms} />}
        </>
      )}

      {option.mWebVariantContent?.contactCapExceededMessage && (
        <div className="mweb-card-content__contact-cap-exceeded">
          {option.mWebVariantContent.contactCapExceededMessage}
        </div>
      )}
      {!option.mWebVariantContent?.contactCapExceededMessage && option.mWebVariantContent?.buttonText && (
        <Button
          shape="square"
          fullWidth
          readonly={!clickButtonToSelect}
          disabled={disabled}
          onClick={() => {
            if (clickButtonToSelect && !option.disabled) {
              onOptionSelect(option.id);
            }
          }}
          {...dataAttrs}
        >
          {option.mWebVariantContent?.buttonText}
        </Button>
      )}

      {showDescriptions && option.mWebVariantContent?.bullets && (
        <div className="mweb-card-content__bullet-wrapper">
          {option.mWebVariantContent?.includesLabel && (
            <div className="mweb-card-content__includes-label">{option.mWebVariantContent.includesLabel}</div>
          )}
          {setupBulletDescription(option.mWebVariantContent.bullets)}
          {option.mWebVariantContent?.showIntegrationsBadge && (
            <div className="mweb-card-content__integrations-badge">
              <Suspense fallback={null}>
                <IntegrationsBadge />
              </Suspense>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
